import React, { useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import { FormComponent, UiComponent } from 'components';
import { PermissionQuery, RoleQuery } from 'queries';
import { IPermission } from 'api/permission';
import { IRole } from 'types';
import useModal from 'hooks/useModal';
import { theme } from 'styles';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import { PERMISSION_MENUS, getActionLabel } from 'data/permission';

const itemsWrap = {
  display: 'flex',
  flexWrap: 'wrap' as const,
  gap: '10px 24px',
};

interface Props {
  role: IRole;
  scopeLabel: string;
}

// 선택한 역할의 권한을 메뉴별 체크박스로 편집
function Permissions({ role, scopeLabel }: Props) {
  const { handleConfirm } = useModal();
  const { data: permissions = [] } = PermissionQuery.useGetAll();
  const { mutateAsync: update, isPending } = RoleQuery.useUpdate(
    role.organizationId,
  );
  const { mutateAsync: remove } = RoleQuery.useDelete(role.organizationId);

  const initial = useMemo(
    () => new Set((role.permissions ?? []).map((p) => p.id)),
    [role.permissions],
  );
  const [selected, setSelected] = useState<Set<number>>(initial);
  const dirty =
    selected.size !== initial.size ||
    Array.from(selected).some((id) => !initial.has(id));

  const groups = useMemo(() => {
    const byResource = new Map<string, IPermission[]>();
    permissions.forEach((p) => {
      const resource = p.code.split(':')[0];
      byResource.set(resource, [...(byResource.get(resource) ?? []), p]);
    });
    const known = new Set(PERMISSION_MENUS.flatMap((m) => m.resources));
    const ordered = PERMISSION_MENUS.map((m) => ({
      key: m.label,
      label: m.label,
      // 리소스별로 한 줄씩
      rows: m.resources
        .map((r) => byResource.get(r) ?? [])
        .filter((row) => row.length > 0),
    }))
      .map((m) => ({ ...m, items: m.rows.flat() }))
      .filter((m) => m.items.length > 0);
    const rest = Array.from(byResource.entries())
      .filter(([r]) => !known.has(r))
      .map(([resource, items]) => ({
        key: resource,
        label: resource,
        rows: [items],
        items,
      }));
    return [...ordered, ...rest];
  }, [permissions]);

  const toggle = (ids: number[], on: boolean) => {
    const next = new Set(selected);
    ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
    setSelected(next);
  };

  const save = () =>
    update({ id: role.id, permissionIds: Array.from(selected) })
      .then(() => toast.success('권한이 저장되었습니다'))
      .catch(() => toast.error(DEFAULT_ERROR_MESSAGE));

  const toggleDisabled = () => {
    const next = !role.disabled;
    handleConfirm({
      confirmContent: `'${role.name}' 역할을 ${next ? '비활성화' : '활성화'}하시겠습니까?`,
      confirmNext: () => {
        update({ id: role.id, disabled: next })
          .then(() =>
            toast.success(next ? '비활성화되었습니다' : '활성화되었습니다'),
          )
          .catch(() => toast.error(DEFAULT_ERROR_MESSAGE));
      },
    });
  };

  const handleDelete = () =>
    handleConfirm({
      confirmContent: `'${role.name}' 역할을 삭제하시겠습니까?`,
      confirmNext: () => {
        remove(role.id)
          .then(() => toast.success('삭제되었습니다'))
          .catch((error) => {
            const message = isAxiosError<{ message?: unknown }>(error)
              ? error.response?.data?.message
              : undefined;
            toast.error(
              typeof message === 'string' ? message : DEFAULT_ERROR_MESSAGE,
            );
          });
      },
    });

  return (
    <div>
      <div
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
          paddingBottom: 16,
          borderBottom: `1px solid ${theme.color.border}`,
        }}
      >
        <div>
          <p css={{ fontSize: 18, fontWeight: 700 }}>
            {role.name}{' '}
            <span css={{ fontSize: 14, color: theme.color.textSub }}>
              ({scopeLabel})
            </span>
          </p>
          {role.description && (
            <p css={{ fontSize: 13, color: theme.color.textSub, marginTop: 4 }}>
              {role.description}
            </p>
          )}
        </div>
        <div css={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <UiComponent.Button
            variant="border"
            color="primary"
            onClick={handleDelete}
          >
            삭제
          </UiComponent.Button>
          <UiComponent.Button
            variant="border"
            color="primary"
            onClick={toggleDisabled}
          >
            {role.disabled ? '활성화' : '비활성화'}
          </UiComponent.Button>
          <UiComponent.Button
            variant="contained"
            color="primary"
            onClick={save}
            disabled={!dirty || isPending}
          >
            저장
          </UiComponent.Button>
        </div>
      </div>

      <div css={{ display: 'flex', flexDirection: 'column' }}>
        {groups.map((g) => {
          const allOn = g.items.every((p) => selected.has(p.id));
          return (
            <div
              key={g.key}
              css={{
                display: 'grid',
                gridTemplateColumns: '180px 1fr',
                alignItems: 'start',
                gap: 12,
                padding: '16px 0',
                borderBottom: `1px solid ${theme.color.border}`,
              }}
            >
              <FormComponent.CheckBox
                checked={allOn}
                handleCheck={(on) =>
                  toggle(
                    g.items.map((p) => p.id),
                    on,
                  )
                }
              >
                <span css={{ fontWeight: 600 }}>{g.label}</span>
              </FormComponent.CheckBox>
              <div css={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {g.rows.map((row) => (
                  <div key={row[0].id} css={itemsWrap}>
                    {row.map((p) => (
                      <FormComponent.CheckBox
                        key={p.id}
                        title={p.description ?? p.code}
                        checked={selected.has(p.id)}
                        handleCheck={(on) => toggle([p.id], on)}
                      >
                        {getActionLabel(p.code)}
                      </FormComponent.CheckBox>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Permissions;
